import { useLayoutEffect, useRef, useState } from 'react';
import { Color, QUICK_VIEW_LIMIT, Size, quickViewsShown, t } from '@bms2/shared';
import { Button } from '../controls';
import styles from './QuickViewBar.module.css';

/**
 * 🔒 The quick-view bar above a list (decision 2880, in the shape decision 1486
 * gave the Companies list's queues): the person's starred views, the first
 * three always and the fourth and fifth where the bar's width holds them. The
 * view being read draws filled navy and pressed, every other one as the
 * secondary button (T761), and a link out to another screen comes last with no
 * pressed state.
 *
 * 🔨 The bar measures its views once in one unwrapped row each time the set of
 * names changes, and then draws as many as fit the width it has; a narrower bar
 * keeps the first three and lets them wrap rather than cutting one.
 */
export interface QuickViewBarProps {
  /** The starred views in the switcher's order. */
  views: readonly { id: string; name: string }[];
  currentId: string;
  onChoose: (id: string) => void;
  /** Links out to another screen at the bar's end, never a view (piece 20). */
  links?: readonly { label: string; route: string }[];
  onRoute?: (route: string) => void;
  /** What a suite reads the bar by: `data-quick-views` carries it. */
  name: string;
}

export function QuickViewBar({ views, currentId, onChoose, links = [], onRoute, name }: QuickViewBarProps) {
  const barRef = useRef<HTMLDivElement | null>(null);
  const candidates = views.slice(0, QUICK_VIEW_LIMIT);
  const signature = candidates.map((view) => `${view.id}:${view.name}`).join('|');
  const [measured, setMeasured] = useState<{ signature: string; edges: number[] } | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const measuring = measured?.signature !== signature;

  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    setWidth(bar.clientWidth);
    if (!measuring) return;
    const items = [...bar.querySelectorAll<HTMLElement>('[data-quick-view]')];
    setMeasured({ signature, edges: items.map((item) => item.offsetLeft + item.offsetWidth) });
  }, [measuring, signature]);

  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setWidth(bar.clientWidth));
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  const shown = measuring || !measured ? candidates.length : quickViewsShown(measured.edges, width);
  return (
    <div ref={barRef} className={styles.bar} role="toolbar" aria-label={t('view.quickViews')} data-quick-views={name} data-measuring={measuring ? 'true' : 'false'} data-quick-views-shown={shown}>
      {candidates.slice(0, shown).map((view) => {
        const current = view.id === currentId;
        return (
          <span key={view.id} className={styles.item} data-quick-view={view.id}>
            <Button size={Size.Small} color={current ? Color.Navy : Color.Inactive} pressed={current} text={[view.name]} actions={[() => onChoose(view.id)]} />
          </span>
        );
      })}
      {links.map((link) => (
        <span key={link.route} className={styles.item} data-quick-view-link={link.route}>
          <Button size={Size.Small} color={Color.Inactive} text={[link.label]} actions={[() => onRoute?.(link.route)]} />
        </span>
      ))}
    </div>
  );
}
